import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Stack, Typography } from '@mui/material'
import {
  getGetOrganisationAiInstructionsQueryKey,
  useCreateOrganisationAiInstruction,
  useDeleteOrganisationAiInstruction,
  useGetOrganisationAiInstructions,
  useUpdateOrganisationAiInstruction,
} from '@api/ai-instructions/ai-instructions'
import type { AIInstructionDTO } from '@api/fastAPI.schemas'
import { Query, useQueryClient } from '@tanstack/react-query'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import ManagerSection from '../ManagerSection'
import AIInstructionList from './components/AIInstructionList'
import useAIInstructionMutationCallbacks from './hooks/useAIInstructionMutationCallbacks'

// Every project's KI-Instruktionen query (key of getGetProjectAiInstructionsQueryKey).
const PROJECT_AI_INSTRUCTIONS_KEY = /^\/projects\/[^/]+\/ai-instructions$/

const isProjectAiInstructionsQuery = ({ queryKey }: Query) =>
  typeof queryKey[0] === 'string' && PROJECT_AI_INSTRUCTIONS_KEY.test(queryKey[0])

interface Props {
  readOnly: boolean
}

/**
 * The organisation's KI-Instruktionen (global Systemmanager). They apply to every project of the
 * organisation that does not ignore them; projects do not get a copy.
 */
const OrganisationAIInstructions = ({ readOnly }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries(getGetOrganisationAiInstructionsQueryKey()),
      // Project views embed the organisation instructions. Their cache never refetches on mount
      // by itself, so refetch the inactive ones too rather than just marking them stale.
      queryClient.invalidateQueries({
        predicate: isProjectAiInstructionsQuery,
        refetchType: 'all',
      }),
    ])
  const mutationCallbacks = useAIInstructionMutationCallbacks(invalidate)

  const { data: instructions, isLoading } = useGetOrganisationAiInstructions<
    AIInstructionDTO[],
    AxiosError
  >({
    query: {
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der KI-Instruktionen'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutateAsync: createInstruction, isLoading: isCreating } =
    useCreateOrganisationAiInstruction<AxiosError, unknown>({
      mutation: {
        meta: {
          title: 'KI-Instruktion hinzufügen',
          description: 'Fügt der Organisation eine KI-Instruktion hinzu',
        },
        ...mutationCallbacks(
          'KI-Instruktion hinzugefügt',
          'Fehler beim Speichern der KI-Instruktion',
        ),
      },
    })

  const {
    mutateAsync: updateInstruction,
    isLoading: isUpdating,
    variables: updateVariables,
  } = useUpdateOrganisationAiInstruction<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'KI-Instruktion bearbeiten',
        description: 'Ändert eine KI-Instruktion der Organisation',
      },
      ...mutationCallbacks(
        'KI-Instruktion gespeichert',
        'Fehler beim Speichern der KI-Instruktion',
      ),
    },
  })

  const {
    mutate: deleteInstruction,
    isLoading: isDeleting,
    variables: deleteVariables,
  } = useDeleteOrganisationAiInstruction<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'KI-Instruktion löschen',
        description: 'Löscht eine KI-Instruktion der Organisation',
      },
      ...mutationCallbacks('KI-Instruktion gelöscht', 'Fehler beim Löschen der KI-Instruktion'),
    },
  })

  if (isLoading) return <LoadingIndicator />

  return (
    <Stack spacing={3} data-cy="organisation-ai-instructions">
      <Typography variant="body2" color="text.secondary">
        Instruktionen, die der KI-Assistent (über MCP) bei der Arbeit in allen Projekten dieser
        Organisation befolgt. Einzelne Projekte können eine Instruktion ignorieren und zusätzlich
        eigene Instruktionen haben.
      </Typography>
      <ManagerSection title="Organisationsweite KI-Instruktionen">
        <AIInstructionList
          instructions={instructions ?? []}
          emptyText="Noch keine KI-Instruktionen vorhanden."
          readOnly={readOnly}
          isCreating={isCreating}
          updatingId={isUpdating ? updateVariables?.instructionId : undefined}
          deletingId={isDeleting ? deleteVariables?.instructionId : undefined}
          onCreate={text => createInstruction({ data: { text } })}
          onUpdate={(instructionId, text) => updateInstruction({ instructionId, data: { text } })}
          onDelete={instructionId => deleteInstruction({ instructionId })}
          addFormId="organisation-ai-instruction-add"
        />
      </ManagerSection>
    </Stack>
  )
}

export default OrganisationAIInstructions
